import { createElement, forwardRef, type HTMLAttributes, type ElementType } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const typographyVariants = cva('text-foreground', {
  variants: {
    variant: {
      'display-2xl': 'font-display font-semibold text-display-2xl',
      'display-xl': 'font-display font-semibold text-display-xl',
      'display-lg': 'font-display font-semibold text-display-lg',
      'display-md': 'font-display font-semibold text-display-md',
      'display-sm': 'font-display font-semibold text-display-sm',
      'heading-lg': 'font-display font-semibold text-heading-lg',
      'heading-md': 'font-display font-semibold text-heading-md',
      'heading-sm': 'font-display font-semibold text-heading-sm',
      'body-lg': 'text-body-lg',
      'body-md': 'text-body-md',
      'body-sm': 'text-body-sm',
      caption: 'text-caption text-muted-foreground',
      eyebrow: 'text-caption font-semibold uppercase tracking-wider text-brand-700',
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    tone: {
      default: '',
      muted: 'text-muted-foreground',
      brand: 'text-brand-700',
      danger: 'text-danger',
    },
  },
  defaultVariants: {
    variant: 'body-md',
    tone: 'default',
  },
});

type TypographyVariantProps = VariantProps<typeof typographyVariants>;

const defaultElement: Record<NonNullable<TypographyVariantProps['variant']>, ElementType> = {
  'display-2xl': 'h1',
  'display-xl': 'h1',
  'display-lg': 'h1',
  'display-md': 'h2',
  'display-sm': 'h2',
  'heading-lg': 'h3',
  'heading-md': 'h4',
  'heading-sm': 'h5',
  'body-lg': 'p',
  'body-md': 'p',
  'body-sm': 'p',
  caption: 'span',
  eyebrow: 'span',
};

export interface TypographyProps
  extends Omit<HTMLAttributes<HTMLElement>, 'color'>, TypographyVariantProps {
  as?: ElementType;
}

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ as, variant, weight, align, tone, className, children, ...props }, ref) => {
    const Component = as ?? defaultElement[variant ?? 'body-md'];
    return createElement(
      Component,
      {
        ref,
        className: cn(typographyVariants({ variant, weight, align, tone }), className),
        ...props,
      },
      children,
    );
  },
);

Typography.displayName = 'Typography';
